import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { serverConsole, useConfigStore } from '@amzn/monera-app-core';
import { Caret, CaretDirection, Carousel } from '@amzn/monera-app-core-components';

import { emitCountMetrics } from '@amzn/monera-app-core/lib/utils/metric-helper';

import { alexaTheme } from '../../theme';
import { TopSkillWidget, WIDGET_WIDTH } from '../top-skill-widget';
import { StyledCarouselButton, StyledText, TopSkillsContainer } from './styles';
import { TopSkillCarouselProps } from './types';
/***
 * availableMetricName: 'TopSkillsShoveler:Available' - Enough Top Skills are present and topSkillsShoveler is shown to Customer, returned top skills are equal or more than 3.
 *
 * unavailableMetricName: 'TopSkillsShoveler:Unavailable' - Not enough Top Skills are present to show to Customer, returned top skills count will be form [0-2] .
 *
 * errorMetricName: 'TopSkillsShoveler:Error' - Error while rendering Top Skills Shoveler.
 */
const TopSkillShovelerMetric = {
  methodName: 'TopSkillsShoveler',
  errorMetricName: 'TopSkillsShoveler:Error',
  availableMetricName: 'TopSkillsShoveler:Available',
  unavailableMetricName: 'TopSkillsShoveler:Unavailable',
  engagementMetricName: 'TopSkillsShoveler:Engagement'
};

// component for rendering top skill shovelers
export const TopSkillsShoveler: FunctionComponent<TopSkillCarouselProps> = (props) => {
  const { topSkills = [], topSkillsRecommendationTitle, showPaginationButtons = true, ...fontSizes } = props;

  const isMobile = !showPaginationButtons;

  const [slidesPerPage, setSlidesPerPage] = useState<number>(5);
  const carouselRef = useRef<HTMLDivElement>(null);
  const imageMargins = 10;
  const [configRegistryState] = useConfigStore();

  const emitMetricsFromTopSkillsShovelerProps = () => {
    async function emitMetricsForTopSkillsShoveler() {
      if (props == null || props?.error) {
        serverConsole.log(
          `Error while rendering Top Skills Shoveler`,
          TopSkillShovelerMetric.methodName,
          TopSkillShovelerMetric.errorMetricName
        );
        await emitCountMetrics(
          TopSkillShovelerMetric.methodName,
          TopSkillShovelerMetric.errorMetricName,
          1,
          configRegistryState.requestId,
          configRegistryState.tenant
        );
      } else {
        // eslint-disable-next-line no-prototype-builtins
        if (!Object.getOwnPropertyDescriptor(props, 'topSkills')) {
          serverConsole.log(
            `Not emitting any metric as the treatment is C for the customer and Top Skills Shoveler is not shown to customer`
          );
        } else if (props.topSkills.length == 0) {
          serverConsole.log(
            `Top Skills Shoveler is not shown to Customer, Empty list of top skills are returned for top skills shoveler`,
            TopSkillShovelerMetric.methodName,
            TopSkillShovelerMetric.unavailableMetricName
          );
          await emitCountMetrics(
            TopSkillShovelerMetric.methodName,
            TopSkillShovelerMetric.unavailableMetricName,
            1,
            configRegistryState.requestId,
            configRegistryState.tenant
          );
        } else {
          serverConsole.log(
            `Top Skills Shoveler is shown to Customer`,
            TopSkillShovelerMetric.methodName,
            TopSkillShovelerMetric.availableMetricName
          );
          await emitCountMetrics(
            TopSkillShovelerMetric.methodName,
            TopSkillShovelerMetric.availableMetricName,
            1,
            configRegistryState.requestId,
            configRegistryState.tenant
          );
        }
      }
    }
    emitMetricsForTopSkillsShoveler()
      .then(() => serverConsole.log('Successfully emitted Top Skills Shoveler Metrics with props: ', props))
      .catch((err: any) => serverConsole.error(`Error while emitting Top Skills Shoveler metrics: ${err}`));
  };

  useEffect(() => {
    emitMetricsFromTopSkillsShovelerProps();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const minSlides = isMobile ? 0 : 3;
      // determines how many widgets fit into current width. (current width) / (widget width + 10 margin)
      carouselRef.current &&
        setSlidesPerPage(
          Math.max(
            minSlides,
            parseFloat(((carouselRef.current.clientWidth + imageMargins) / (WIDGET_WIDTH + imageMargins)).toFixed(2))
          )
        );
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const impressionMetric = () => {
    async function emitMetricsForTopSkillShovelersEngagement() {
      serverConsole.log(
        `Engaged with a skill in Top Skills Shoveler`,
        TopSkillShovelerMetric.methodName,
        TopSkillShovelerMetric.engagementMetricName
      );
      await emitCountMetrics(
        TopSkillShovelerMetric.methodName,
        TopSkillShovelerMetric.engagementMetricName,
        1,
        configRegistryState.requestId,
        configRegistryState.tenant
      );
    }
    emitMetricsForTopSkillShovelersEngagement()
      .then(() => serverConsole.log('Successfully emitted Top Skills Shoveler Engagement Metrics'))
      .catch((err: any) => serverConsole.error(`Error while emitting Top Skills Shoveler Engagement metrics: ${err}`));
  };

  const topSkillWidgets = topSkills.map((props, i) => (
    <div data-cy={`top-skill-${i}`} key={props.title} style={{ width: 'auto' }}>
      <TopSkillWidget
        {...props}
        {...fontSizes}
        onTopSkillsWidgetClick={() => {
          impressionMetric();
        }}
      />
    </div>
  ));

  return topSkills.length > 0 ? (
    <TopSkillsContainer isMobile={isMobile} data-cy={'top-skills'}>
      <StyledText
        bold
        fontSize={isMobile ? alexaTheme.typography?.mobileH2?.fontSize : alexaTheme.typography?.desktopH2?.fontSize}
        aria-level={2}
      >
        {topSkillsRecommendationTitle}
      </StyledText>
      <div ref={carouselRef} style={{ marginLeft: 5 }}>
        <Carousel
          useAutoSlideWidth={false}
          allowScrolling={isMobile}
          slidesPerPage={slidesPerPage}
          showPaginationDots={false}
          circularPaging={false}
          paginateWholePage={true}
          showPaginationButtons={showPaginationButtons}
          imageMargins={`${imageMargins}px`}
          renderRightPaginationButton={(onClick) => (
            <StyledCarouselButton
              isBorderLeft
              data-cy={'top-skills-carousel-left-button'}
              role="button"
              onClick={onClick}
            >
              <Caret linkColor={'#7a7a7a'} rotation={CaretDirection.Right} />
            </StyledCarouselButton>
          )}
          renderLeftPaginationButton={(onClick) => (
            <StyledCarouselButton
              data-cy={'top-skills-carousel-right-button'}
              isBorderLeft={false}
              role="button"
              onClick={onClick}
            >
              <Caret linkColor={'#7a7a7a'} rotation={CaretDirection.Left} />
            </StyledCarouselButton>
          )}
          elements={topSkillWidgets}
          leftPaginationButtonDistance="0px"
          rightPaginationButtonDistance="0px"
          useFillerSlides={false}
          hidePaginationButtonsAtEnds={false}
          hideLeftPaginationButtonOnFirstLoad={true}
          paginationButtonEdgeStyles={{ opacity: 0.5 }}
        />
      </div>
    </TopSkillsContainer>
  ) : null;
};

export * from './types';
